import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Search, Users } from "lucide-react";
import { Brand } from "@/components/Layout";
import { FloorMap, tableLabel } from "@/components/FloorMap";
import { API, api, errMsg } from "@/lib/api";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
const where = (t) => `${t.y < 34 ? "in alto" : t.y > 66 ? "in basso" : "al centro"}${t.x < 34 ? " a sinistra" : t.x > 66 ? " a destra" : ""} nella mappa`;

export default function Hostess() {
  const { id, token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);
  const [offline, setOffline] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  useEffect(() => {
    const key = `hostess:${token || id}`;
    const src = (p) => (token ? `${API}/public/hostess/${token}/image` : `${API}/floorplans/${p.id}/image`);
    const fromCache = async () => {
      const raw = localStorage.getItem(key);
      if (!raw) return false;
      const p = JSON.parse(raw);
      setD(p);
      setOffline(true);
      if (p.plan && window.caches) {
        const hit = await (await caches.open("hostess-maps")).match(src(p.plan));
        if (hit) setImgUrl(URL.createObjectURL(await hit.blob()));
      }
      return true;
    };
    api.get(token ? `/public/hostess/${token}` : `/contracts/${id}/seating`).then(async (r) => {
      setD(r.data);
      setOffline(false);
      localStorage.setItem(key, JSON.stringify({ ...r.data, hostess_token: undefined }));
      if (r.data.plan) {
        const u = src(r.data.plan);
        setImgUrl(u);
        try {
          const res = await fetch(u, { credentials: "include" });
          if (res.ok && window.caches) await (await caches.open("hostess-maps")).put(u, res);
        } catch {
          /* offline cache best effort */
        }
      }
    }).catch(async (e) => { if (e.response || !(await fromCache())) setErr(errMsg(e)); });
  }, [id, token]);
  const byLabel = useMemo(() => Object.fromEntries((d?.plan?.tables || []).map((t) => [tableLabel(t), t])), [d]);
  const results = useMemo(() => {
    const n = norm(q);
    if (n.length < 2 || !d) return [];
    const words = n.split(/\s+/);
    return d.guests.filter((g) => words.every((w) => norm(g.nome).includes(w))).slice(0, 8);
  }, [q, d]);
  const g = pick || (results.length === 1 ? results[0] : null);
  const table = g && byLabel[g.tavolo];
  const mates = g ? d.guests.filter((x) => x.tavolo && x.tavolo === g.tavolo) : [];
  if (err) return <div className="p-8 text-[#991B1B]" data-testid="hostess-error">{err}</div>;
  if (!d) return <div className="p-8 text-sm text-[#7B929C]">Caricamento…</div>;
  return (
    <div className="min-h-screen bg-[#0D2B3A] text-white" data-testid="hostess-page">
      <header className="px-4 py-3 flex items-center justify-between gap-3 border-b border-white/10">
        <div className="bg-white rounded-full px-3 py-1"><Brand small /></div>
        <div className="text-right text-xs text-white/70 truncate">{offline && <span className="mr-2 rounded-full bg-[#B45309] px-2 py-0.5 text-white" data-testid="hostess-offline">senza rete • dati salvati</span>}{d.evento.cliente_nome} • {d.plan?.nome || "sala"}</div>
      </header>
      <main className="max-w-5xl mx-auto p-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0B6E8F]" size={22} />
          <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setPick(null); }} placeholder="Nome o cognome dell'ospite…" className="w-full rounded-2xl bg-white text-[#0D2B3A] text-xl pl-12 pr-4 py-4 outline-none focus:ring-4 focus:ring-[#34B3B0]/50" data-testid="hostess-search" />
        </div>
        {results.length > 1 && !pick && <div className="grid sm:grid-cols-2 gap-2" data-testid="hostess-results">{results.map((x, i) => <button key={i} onClick={() => setPick(x)} className="text-left rounded-xl bg-white/10 hover:bg-white/20 p-3 transition-colors" data-testid={`hostess-result-${i}`}><div className="font-medium">{x.nome}</div><div className="text-sm text-[#9FE3E0]">{x.tavolo || "Tavolo da assegnare"}</div></button>)}</div>}
        {q.length >= 2 && !results.length && <div className="rounded-xl bg-[#E07A5F]/20 p-4" data-testid="hostess-none">Nessun ospite con questo nome. Prova con il solo cognome.</div>}
        {g && (
          <div className="rounded-2xl bg-white text-[#0D2B3A] p-5 flex flex-wrap items-center justify-between gap-4 fade-up" data-testid="hostess-answer">
            <div><div className="text-sm text-[#7B929C]">{g.nome}{g.tipo === "bambino" && " • bambino"}</div><div className="font-serif-it text-5xl font-semibold" data-testid="hostess-table">{g.tavolo || "Da assegnare"}</div>{g.allergie && <div className="text-sm text-[#B45309] mt-1">⚠ {g.allergie}</div>}{table && <div className="text-sm text-[#0B6E8F] mt-1" data-testid="hostess-where">📍 {where(table)}{table.sposi ? " • tavolo sposi" : ""}</div>}</div>
            {mates.length > 1 && <div className="text-xs text-[#47606B] max-w-xs"><Users size={13} className="inline mr-1" />Al tavolo con: {mates.filter((m) => m.nome !== g.nome).map((m) => m.nome).join(", ")}</div>}
          </div>
        )}
        {d.plan ? <FloorMap plan={d.plan} src={imgUrl} tables={d.plan.tables} used={new Set(d.table_ids)} highlight={table?.id} testId="hostess-map" />
          : <div className="rounded-xl bg-white/10 p-4 text-sm">Nessuna mappa sala collegata a questo evento.</div>}
      </main>
    </div>
  );
}
